import React from "react";

/**
 * Cross-browser time input that always displays 12-hour format with AM/PM.
 * Replaces native type="time" which renders inconsistently (Safari/macOS shows numbers only).
 * Value format: HH:mm (24-hour, e.g. "09:00", "17:00")
 */
const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 00, 01, 02, ..., 59

function parseHhMm(value) {
  const str = String(value || "").trim();
  const match = str.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return { hour12: 12, minute: 0, ampm: "AM" };
  let h24 = parseInt(match[1], 10);
  const m = Math.min(59, Math.max(0, parseInt(match[2], 10) || 0));
  if (h24 === 0) h24 = 12;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const hour12 = h24 > 12 ? h24 - 12 : h24 === 0 ? 12 : h24;
  return { hour12, minute: m, ampm };
}

function toHhMm(hour12, minute, ampm) {
  let h24 = hour12;
  if (ampm === "PM" && hour12 !== 12) h24 = hour12 + 12;
  if (ampm === "AM" && hour12 === 12) h24 = 0;
  const h = String(h24).padStart(2, "0");
  const m = String(Math.min(59, Math.max(0, minute || 0))).padStart(2, "0");
  return `${h}:${m}`;
}

export default function TimeInputWithAmPm({ value, onChange, className = "" }) {
  const { hour12, minute, ampm } = parseHhMm(value);

  const handleChange = (field, val) => {
    let newHour = hour12;
    let newMinute = minute;
    let newAmpm = ampm;
    if (field === "hour") newHour = parseInt(val, 10);
    if (field === "minute") newMinute = parseInt(val, 10);
    if (field === "ampm") newAmpm = val;
    onChange(toHhMm(newHour, newMinute, newAmpm));
  };

  const displayMinute = MINUTES.includes(minute) ? minute : Math.min(59, Math.max(0, minute));

  return (
    <div className={`time-input-ampm ${className}`.trim()}>
      <select
        className="time-input-ampm-select"
        value={hour12}
        onChange={(e) => handleChange("hour", e.target.value)}
        aria-label="Hour"
      >
        {HOURS_12.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="time-input-ampm-separator">:</span>
      <select
        className="time-input-ampm-select"
        value={displayMinute}
        onChange={(e) => handleChange("minute", e.target.value)}
        aria-label="Minute"
      >
        {MINUTES.map((m) => (
          <option key={m} value={m}>
            {String(m).padStart(2, "0")}
          </option>
        ))}
      </select>
      <select
        className="time-input-ampm-select time-input-ampm-meridiem"
        value={ampm}
        onChange={(e) => handleChange("ampm", e.target.value)}
        aria-label="AM/PM"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
